import React, { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from 'react-router-dom';
import { FaSave } from 'react-icons/fa';
import { TemplateQuestionList, fillNegotiationQuestionnaire } from '../../../../services/Templates';
import { toast } from 'react-toastify';
import { CiCalendar } from 'react-icons/ci';
import { CustomDatePicker } from '../../../../components';

const NegotiationStep3 = ({ formObj, setFormObj, setLoading, nextStep, prevStep }) => {
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [livePreview, setLivePreview] = useState("");

  const templateUuid = formObj?.template_uuid;
  const negotiationId = formObj?.negotiation_id;
  const htmlContentFromStep2 = formObj?.html_content;
  const apiAnswers = formObj?.answers || [];
  const mountedRef = useRef(true);
  const dateRefs = useRef({});
  const fieldRefs = useRef({});

  // Helper to format variable names to readable labels
  const formatLabel = (variableName = '') =>
    variableName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Initialize live preview content from the formObj or session storage
  useEffect(() => {
    const htmlFromTemplate =
      formObj?.html_content ||
      sessionStorage.getItem("template_html_content") || "";

    setLivePreview(highlightVariables(htmlFromTemplate, formData));
  }, [formObj, formData]);


  const updateLivePreview = (fieldName, value) => {
    const placeholder = new RegExp(`{{\\s*${fieldName}\\s*}}`, "gi");
    setLivePreview(prev => prev.replace(placeholder, value || ""));
  };

  const highlightVariables = (html, formValues) => {
    if (!html) return html;

    Object.keys(formValues).forEach((key) => {
      const placeholder = `{{${key}}}`;
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, "gi");
      const value = formValues[key];

      if (value && value.trim() !== "") {
        html = html.replace(
          regex,
          `<span 
            class="placeholder-filled" 
            data-var="${key}"
        >${value}</span>`
        );
      } else {
        html = html.replace(
          regex,
          `<span 
            class="placeholder-unfilled" 
            data-var="${key}"
        >${placeholder}</span>`
        );
      }
    });

    return html;
  };

  const handlePreviewClick = (e) => {
    const target = e.target;

    if (target.dataset && target.dataset.var) {
      const varName = target.dataset.var;

      if (fieldRefs.current[varName]) {
        fieldRefs.current[varName].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        fieldRefs.current[varName].classList.add("field-highlight");

        setTimeout(() => {
          fieldRefs.current[varName]?.classList.remove("field-highlight");
        }, 1500);
      }
    }
  };


  // Keep track of the latest formObj without triggering effects
  const latestFormObj = useRef(formObj);
  useEffect(() => {
    latestFormObj.current = formObj;
  });

  // Fetch template questions on component mount or when templateUuid changes
  useEffect(() => {
    mountedRef.current = true;

    if (!templateUuid || !negotiationId) {
      setError('Template or negotiation data is missing');
      setLoading && setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchQuestions = async () => {
      try {
        setError(null);
        setLoading && setLoading(true);

        const response = await TemplateQuestionList({ template_uuid: templateUuid });
        const questionsData = response?.data?.data || [];

        if (cancelled) return;

        if (Array.isArray(questionsData) && questionsData.length > 0) {
          setQuestions(questionsData);

          // Prepare formData initial values using the latest formObj
          const currentFormObj = latestFormObj.current;
          const initialFormData = {};
          
          questionsData.forEach((question) => {
            let value = currentFormObj?.questionnaire_data?.[question.variable_name];
            const apiAnswer = currentFormObj?.answers?.find((a) => a.question_id === question.id);
            if (value === undefined || value === null) {
              value = apiAnswer?.answer_text || '';
            }
            // Format date strings if applicable
            if (question.variable_name.toLowerCase().includes('date') && value) {
              const d = new Date(value);
              if (!isNaN(d)) value = d.toISOString().split('T')[0];
            }
            initialFormData[question.variable_name] = value;
          });
          setFormData(initialFormData);
        } else {
          setQuestions([]);
          setError('No questions found for this template');
        }
      } catch (err) {
        console.error('Error fetching questions:', err);
        setError('Failed to load questions. Please try again.');
      } finally {
        if (!cancelled && setLoading) setLoading(false);
      }
    };

    fetchQuestions();

    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
    // Only re-run if templateUuid or negotiationId changes
  }, [templateUuid, negotiationId, setLoading]);

  // Determine input field type based on variable name
  const getFieldType = (variableName = '') => {
    const lower = variableName.toLowerCase();
    if (lower.includes('date')) return 'date';
    if (lower.includes('description') || lower.includes('address') || lower.includes('terms') || lower.includes('notes'))
      return 'textarea';
    if (lower.includes('email')) return 'email';
    if (lower.includes('phone') || lower.includes('number')) return 'tel';
    if (lower.includes('amount') || lower.includes('price') || lower.includes('total')) return 'number';
    return 'text';
  };

  // Checks if a field is required based on keywords
  const isRequired = (variableName = '') => {
    const requiredKeywords = ['name', 'amount', 'party', 'effective'];
    const lower = variableName.toLowerCase();
    return requiredKeywords.some(k => lower.includes(k));
  };

  // Handle text and number input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const updated = { ...formData, [name]: value };

    setFormData(updated);

    // Apply highlight + live update
    const rawTemplate =
      formObj?.html_content ||
      sessionStorage.getItem("template_html_content") ||
      "";

    setLivePreview(highlightVariables(rawTemplate, updated));
  };


  // Validate form fields
  const validateForm = useCallback(() => {
    const errors = {};

    questions.forEach(q => {
      const varName = q.variable_name;
      const raw = formData[varName];
      const value = typeof raw === 'string' ? raw.trim() : raw;

      if (isRequired(varName) && (!value && value !== 0)) {
        errors[varName] = `${formatLabel(varName)} is required`;
        return;
      }

      if (value && getFieldType(varName) === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errors[varName] = 'Please enter a valid email address';
        }
      }

      if (value && getFieldType(varName) === 'number') {
        if (isNaN(Number(value))) {
          errors[varName] = `${formatLabel(varName)} must be a number`;
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, questions]);

  // Go back handler
  const handleBack = (e) => {
    e && e.preventDefault();
    setFormObj(prev => ({ ...prev, questionnaire_data: formData }));
    if (prevStep) prevStep();
    else navigate(-1);
  };

  // Submit form handler
  const handleSubmit = async (e, saveOnly = false) => {
    e && e.preventDefault();

    if (!saveOnly && !validateForm()) return;

    const answers = questions
      .map(q => {
        const value = (formData[q.variable_name] || '').toString().trim();
        return {
          question_id: q.id,
          answer_text: value,
        };
      })
      .filter(a => a.answer_text !== '');

    const payload = {
      negotiation_id: negotiationId,
      answers,
      save_only: Boolean(saveOnly),
    };

    try {
      setIsSubmitting(true);
      setLoading && setLoading(true);

      const response = await fillNegotiationQuestionnaire(payload);

      sessionStorage.setItem('step3_formData', JSON.stringify(formData));

      const htmlContentFromAPI =
        response?.data?.html_content ??
        response?.data?.data?.html_content ??
        response?.data?.data?.template_content ??
        response?.data?.template_content ??
        '';

      const finalHtmlContent =
        htmlContentFromAPI || htmlContentFromStep2 || sessionStorage.getItem('template_html_content') || '';

      setFormObj(prev => ({
        ...prev,
        questionnaire_data: formData,
        html_content: finalHtmlContent,
      }));

      if (saveOnly) {
        toast.success('Progress saved successfully!');
        navigate('/client-portal/negotiation-contract');
      } else {
        toast.success('Questionnaire submitted successfully!');
        if (nextStep) nextStep();
      }
    } catch (err) {
      console.error('Error submitting questionnaire:', err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to submit questionnaire. Please try again.';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
      setLoading && setLoading(false);
    }
  };

  const handleDateChange = (name, date) => {
    const formatted = date ? date.toISOString().split("T")[0] : "";
    setFormData(prev => ({ ...prev, [name]: formatted }));
    updateLivePreview(name, formatted);
  };

  const renderField = (question) => {
    const name = question.variable_name;
    const fieldType = getFieldType(name);
    const label = formatLabel(name);
    const required = isRequired(name);
    const value = formData[name] ?? '';
    const errorText = validationErrors[name];

    if (fieldType === 'textarea') {
      return (
        <div className="col-md-12 mb-3">
          <div className="negotiation-step3-form-group-full">
            <label
              className="negotiation-label"
              key={question.id}
              ref={(el) => (fieldRefs.current[name] = el)}
            >
              {label}{required && <span className="required-asterisk"> *</span>}
            </label>

            <textarea
              name={name}
              placeholder={question.question_text || ''}
              value={value}
              onChange={handleInputChange}
              className={`negotiation-textarea ${errorText ? 'input-error' : ''}`}
              rows={4}
            />

            {errorText && <div className="field-error">{errorText}</div>}
          </div>
        </div>
      );
    }

    if (fieldType === 'date') {
      return (
        <div className="col-md-12 mb-3">
        <CustomDatePicker
          label={label}
          name={name}
          selected={value ? new Date(value) : null}
          onChange={(date) => handleDateChange(name, date)}
          placeholder={question.question_text || ""}
          required={required}
          error={errorText}
          ref={(el) => (fieldRefs.current[name] = el)}
          minDate={new Date()}
        />
      </div>
      );
    }

    // Default input field
    return (
      <div className="col-md-12 mb-3">
        <label
          className="negotiation-label"
          key={question.id}
          ref={(el) => (fieldRefs.current[name] = el)}
        >
          {label}{required && <span className="required-asterisk"> *</span>}
        </label>

        <input
          type={fieldType}
          name={name}
          placeholder={question.question_text || ''}
          value={value}
          onChange={handleInputChange}
          className={`negotiation-input ${errorText ? 'input-error' : ''}`}
        />

        {errorText && <div className="field-error">{errorText}</div>}
      </div>
    );

  };

  // Error state UI
  if (error) {
    return (
      <div className="error-container text-center py-5 col-md-12 m-0 p-0">
        <div className="error-icon mb-3">
          <i className="bi bi-exclamation-triangle text-danger display-4 mb-3"></i>
          <h3 className="text-danger mb-3">{error}</h3>
        </div>
        <div className="d-flex justify-content-center gap-3 mt-3">
          <button
            onClick={() => {
              setError(null);
              setTimeout(() => { }, 0);
            }}
            className="btn btn-outline-danger"
          >
            <i className="bi bi-arrow-repeat"></i> Retry
          </button>
          <button onClick={handleBack} className="btn btn-secondary">
            <i className="bi bi-arrow-left"></i> Go Back
          </button>
        </div>
      </div>
    );
  }

  // No questions UI
  if (!questions || questions.length === 0) {
    return (
      <div className="no-questions-container text-center py-5 col-md-12 m-0 p-0">
        <div className="no-questions-icon mb-3">
          <i className="bi bi-file-earmark-text step3-empty-icon display-4"></i>
          <h3 className="fw-semibold step3-empty-title pb-3 fs-2 my-3">
            No questions available for this template
          </h3>
        </div>
        <div className="d-flex justify-content-center">
          <button onClick={handleBack} className="btn btn-secondary">
            <i className="bi bi-arrow-left"></i> Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="negotiation-step1-header">
        <div className="negotiation-header-left">
          <h2 className="step1-title">Fill template variables</h2>
          <p className="step1-subtitle">Please answer the following questions to generate the negotiation.</p>
        </div>
        <div className="negotiation-header-right">
          <span className="step-indicator">Step 3 of 6</span>
        </div>
      </div>

      <div className="negotiation-step3-container">
        <div className="negotiation-step3-content">
          <div className="form-section">
            <h3 className="section-title">Fill template variables</h3>
            <div className="form-grid">
              {questions.map(q => renderField(q))}
            </div>
          </div>

          <div className="preview-section">
            <h3 className="section-title preview-title">Live contract preview</h3>
            <div
              className="template-preview-box"
              onClick={handlePreviewClick}
              dangerouslySetInnerHTML={{ __html: livePreview }}
            />

          </div>
        </div>
      </div>
      <div className="sleeve-step-buttons mt-3">
        <button onClick={handleBack} className="sleeve-btn-back" type="button">
          Back
        </button>

        <div className="sleeve-step-buttons-right">
          <button
            onClick={(e) => handleSubmit(e, true)}
            className="sleeve-btn-save"
            type="button"
            disabled={isSubmitting}
            aria-disabled={isSubmitting}
          >
            <FaSave /> Save & Exit
          </button>

          <button
            onClick={() => handleSubmit(null, false)}
            className="sleeve-btn-next"
            type="button"
            disabled={isSubmitting}
          >
            Next
          </button>

        </div>
      </div>
    </>
  );
};

export default NegotiationStep3;
